import styles from './BlogArticle.module.scss'
import dayjs from 'dayjs'
import Head from 'next/head'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { generateArticleStructuredData } from 'utils/structuredData'
import { ArticleBody, ArticleFooter, ArticleHeader, ArticleIntro, ArticleValve } from 'components/article'
import ContentBlocks from 'components/article/content-blocks/ContentBlocks'
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs'
import ArticleSection from 'components/article/article-section/ArticleSection'
import { BlogArticleProps } from 'features/blog/article/types'
import { mapAndSortCmsBlogArticlesToArticles } from 'utils/cms'
import { Img } from '@boltenergy-be/design-system'

const BlogArticle = ({ article, otherArticles }: BlogArticleProps) => {
  // i18n
  const { t } = useTranslation(['blog', 'common'])

  // Next router
  const { asPath, locale } = useRouter()

  return (
    <main className={styles['blog-article']}>
      {/* SEO */}
      <Head>
        <script
          type="application/ld+json"
          key="article-jsonld"
          dangerouslySetInnerHTML={generateArticleStructuredData({
            title: article.attributes.title,
            authorName: article.attributes.author.data?.attributes.firstName,
            datePublished: article.attributes.customPublishedAt ?? article.attributes.publishedAt,
            dateUpdated: article.attributes.updatedAt,
            thumbnail: article.attributes.thumbnail.data?.attributes.url,
            type: 'NewsArticle'
          })}
        />
        <link rel="canonical" href={`https://www.boltenergie.be/${locale}${asPath}`} />
      </Head>

      {/* BREADCRUMBS */}
      <nav className={classNames('container', styles['blog-nav'])}>
        <Breadcrumbs className={styles.breadcrumbs} title={article.attributes.title} />
      </nav>

      {/* HERO */}
      <section className={classNames('container', styles.hero)}>
        <Img
          publicId={
            article.attributes.heroImage.data?.attributes.provider_metadata.public_id ||
            article.attributes.thumbnail.data.attributes.provider_metadata.public_id
          }
          priority
          fill
          sizes="100vw"
          alt=""
        />
      </section>

      {/* CONTENT */}
      <section className={classNames('container', styles.article)}>
        {/* HEADER */}
        <ArticleHeader
          author={{
            date: dayjs(article.attributes.customPublishedAt ?? article.attributes.publishedAt).format('DD/MM/YYYY'),
            image: article.attributes.author.data?.attributes.avatar.data?.attributes.url,
            name: article.attributes.author.data?.attributes.firstName
          }}
          tag={article.attributes.category.data?.attributes.title}
          title={article.attributes.title}
        />

        {/* INTRO */}
        <ArticleIntro>{parse(article.attributes.description)}</ArticleIntro>

        {/* BODY */}
        <ArticleBody>
          <ContentBlocks contentBlocks={article.attributes.contentBlocks ?? []} />
        </ArticleBody>

        {/* VALVE (afsluiter) */}
        <ArticleValve>
          {article.attributes.closingText
            ? parse(article.attributes.closingText)
            : t(
                'articleValve',
                'Bolt helpt jou wegwijs maken in energie. Inzicht in je energie-aansluiting en energie algemeen helpen jou om de juiste keuzes te maken richting een klimaatneutrale toekomst.'
              )}
        </ArticleValve>

        {/* FOOTER */}
        <ArticleFooter />
      </section>

      {/* MORE ARTICLES */}
      <ArticleSection
        title={t('common:readMore', 'Meer lezen?')}
        articles={mapAndSortCmsBlogArticlesToArticles(otherArticles)}
        className={styles['more-articles']}
      />
    </main>
  )
}

export default BlogArticle
